import { Button, InputNumber, InputCurrency } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { v4 } from "uuid";
import { SpinnerLegacy, StockIndicator } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderItemDelete } from "../../../hooks/api/v2/orderItem/useOrderItemDelete";
import { useOrderItemUpdate } from "../../../hooks/api/v2/orderItem/useOrderItemUpdate";
import { useBuyerProduct } from "../../../hooks/api/v2/product/useBuyerProduct";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../hooks/usePriceFormat";
import { OrderEditItemInsufficientStockModal } from "./OrderEditItemInsufficientStockModal";

type OrderEditItemEditFormValues = {
  price: string;
  quantity: number;
};

export const OrderEditItemEdit = ({
  orderItem,
  onCancel,
  onUpdate,
  onDelete,
  setShowCheckoutIncreaseAlert,
}: {
  orderItem: OrderGetResponseType["OrderItems"][number];
  onCancel: () => void;
  onUpdate?: () => void;
  onDelete?: () => void;
  setShowCheckoutIncreaseAlert: (showCheckoutIncreaseAlert: boolean) => void;
}) => {
  const orderItemUpdateMutation = useOrderItemUpdate(orderItem.id);
  const deleteOrderItem = useOrderItemDelete(orderItem.id);

  const productQuery = useBuyerProduct(orderItem.ProductId);

  const [stockDialogActive, setStockDialogActive] = useState<boolean>(false);
  const form = useForm<OrderEditItemEditFormValues>({
    defaultValues: {
      price: formatPriceApiToInput(orderItem.price),
      quantity: orderItem.quantity,
    },
    shouldUseNativeValidation: true,
  });

  const price = useWatch({
    control: form.control,
    name: "price",
  });
  const quantity = useWatch({
    control: form.control,
    name: "quantity",
  });
  const apiPrice = formatPriceInputToApi(price);
  const formId = v4();

  const onSubmit = (values: OrderEditItemEditFormValues) => {
    const apiPrice = formatPriceInputToApi(values.price);

    if (apiPrice === null) throw new Error("Price not defined");

    orderItemUpdateMutation.mutate(
      { price: apiPrice, quantity: values.quantity },
      {
        onSuccess: () => {
          onUpdate?.();
          setShowCheckoutIncreaseAlert(false);
        },
        onError: (error) => {
          const message =
            apiV2AssertError(error)?.response?.data.message ??
            "An unknown error occurred";
          if (message === "Can not increase price of a checkout order.") {
            setShowCheckoutIncreaseAlert(true);
            form.setValue("price", formatPriceApiToInput(orderItem.price));
            form.setValue("quantity", orderItem.quantity);
          }
        },
      },
    );
  };

  if (
    orderItemUpdateMutation.isPending ||
    deleteOrderItem.isPending ||
    productQuery.isLoading
  )
    return <SpinnerLegacy />;

  if (productQuery.isFetched) {
    return (
      <>
        {orderItemUpdateMutation.isError && (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(orderItemUpdateMutation.error)?.response?.data
                .message ?? "An unknown error occurred"
            }
          />
        )}
        {deleteOrderItem.isError && (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(deleteOrderItem.error)?.response?.data.message ??
              "An unknown error occurred"
            }
          />
        )}
        {productQuery.isError && (
          <Alert
            message={
              apiV2AssertError(productQuery.error)?.response?.data.message ??
              "Unknown error occurred."
            }
          />
        )}
        {productQuery.isSuccess && (
          <div className={"flex flex-col gap-4"}>
            <form
              id={formId}
              className={"flex flex-wrap items-center gap-x-4 gap-y-2"}
            >
              <div className={"w-[5rem]"}>
                <InputLabel>Qty</InputLabel>
                <InputContainer>
                  <InputNumber
                    {...form.register("quantity", {
                      required: true,
                      min: 1,
                      valueAsNumber: true,
                    })}
                  />
                </InputContainer>
              </div>
              <div className={"min-w-[200px] flex-1"}>
                <InputLabel>Product item name</InputLabel>
                <InputContainer
                  className={
                    "flex h-[46px] items-center justify-between border-none"
                  }
                >
                  <span>{orderItem.Product.name}</span>
                  {productQuery.data && (
                    <StockIndicator
                      stock={productQuery.data.availableStock}
                      type={"short"}
                    />
                  )}
                </InputContainer>
              </div>
              <div>
                <InputLabel>Price (per unit)</InputLabel>
                <InputContainer className={"w-[8rem]"}>
                  <InputCurrency
                    showPrefix
                    allowNegative
                    {...form.register("price", {
                      required: true,
                      min: 0,
                      valueAsNumber: true,
                    })}
                  />
                </InputContainer>
              </div>
              <div>
                <InputLabel>Line price</InputLabel>
                <InputContainer
                  className={"flex h-[46px] items-center border-none"}
                >
                  {typeof apiPrice === "number" &&
                    formatCurrency(apiPrice * quantity)}
                </InputContainer>
              </div>
            </form>
            <div className={"items-right flex justify-end gap-2"}>
              <Button
                onClick={() => {
                  onCancel();
                  setShowCheckoutIncreaseAlert(false);
                }}
                color={"transparent"}
                size={"small"}
              >
                Cancel
              </Button>
              <Button
                size={"small"}
                color={"red"}
                onClick={() =>
                  deleteOrderItem.mutate(undefined, {
                    onSuccess: onDelete,
                  })
                }
              >
                Delete
              </Button>
              <Button
                size={"small"}
                disabled={
                  apiPrice === orderItem.price &&
                  quantity === orderItem.quantity
                }
                form={formId}
                onClick={form.handleSubmit((data) => {
                  if (
                    !orderItem.Product.canBackorder &&
                    data.quantity >
                      orderItem.quantity + productQuery.data.availableStock
                  ) {
                    return setStockDialogActive(true);
                  }
                  onSubmit(data);
                })}
              >
                Update
              </Button>
              <OrderEditItemInsufficientStockModal
                product={productQuery.data}
                onConfirm={form.handleSubmit((data) => {
                  setStockDialogActive(false);
                  onSubmit(data);
                })}
                onDismiss={() => setStockDialogActive(false)}
                quantity={quantity}
                active={stockDialogActive}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  return null;
};
